import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Hooks for `GET /api/platform/metrics/*` (super-admin telemetry).
 *
 * The shapes here mirror `app.domains.platform.schemas` exactly. Keeping
 * the types co-located with the query factory lets the hero dashboard
 * stay self-contained — no @eduviaos/shared types to add until a second
 * portal needs the same telemetry.
 *
 * Cache: 60s stale-time. The dashboard is read-mostly; signups and
 * tenant counts don't move on the second-by-second timescale.
 */

export type TenantsMetric = {
  total: number;
  active: number;
  suspended: number;
  archived: number;
};

export type VerticalSegment = {
  vertical: string;
  label: string;
  count: number;
};

export type VerticalsMetric = {
  segments: VerticalSegment[];
  total: number;
};

export type SignupsMonthBucket = {
  month: string;
  vertical: string | null;
  count: number;
};

export type SignupsByMonthMetric = {
  months: SignupsMonthBucket[];
  range_start: string;
  range_end: string;
};

const ONE_MINUTE = 60 * 1000;

export function useTenantsMetric() {
  return useQuery<TenantsMetric>({
    queryKey: ["platform", "metrics", "tenants"],
    queryFn: () => api.get<TenantsMetric>("/platform/metrics/tenants"),
    staleTime: ONE_MINUTE,
    gcTime: 5 * ONE_MINUTE,
  });
}

export function useVerticalsMetric() {
  return useQuery<VerticalsMetric>({
    queryKey: ["platform", "metrics", "verticals"],
    queryFn: () => api.get<VerticalsMetric>("/platform/metrics/verticals"),
    staleTime: ONE_MINUTE,
    gcTime: 5 * ONE_MINUTE,
  });
}

export function useSignupsByMonthMetric(
  opts: {
    months?: number;
    groupByVertical?: boolean;
  } = {},
) {
  const months = opts.months ?? 6;
  const groupByVertical = opts.groupByVertical ?? true;
  const params = new URLSearchParams({
    months: String(months),
    group_by_vertical: String(groupByVertical),
  });
  return useQuery<SignupsByMonthMetric>({
    queryKey: ["platform", "metrics", "signups-by-month", months, groupByVertical],
    queryFn: () => api.get<SignupsByMonthMetric>(`/platform/metrics/signups-by-month?${params}`),
    staleTime: ONE_MINUTE,
    gcTime: 5 * ONE_MINUTE,
  });
}
